import { Extension, isNodeEmpty } from '@tiptap/core';
import { type EditorState, Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface PlaceholderOptions {
  text?: string;
  /** Show only when the whole doc is empty, not on every empty line */
  onlyWhenEmpty?: boolean;
}

function getNodePlaceholderText(
  state: EditorState,
  defaultText?: string
): string {
  const $pos = state.doc.resolve(state.selection.from);
  const node = $pos.parent;

  if (node.type.name === 'heading') return `Heading ${node.attrs.level}`;
  if (node.type.name === 'codeBlock') return '';

  for (let d = $pos.depth - 1; d >= 0; d--) {
    const name = $pos.node(d).type.name;
    if (name === 'bulletList' || name === 'orderedList') return 'Item';
    if (name === 'taskList') return 'To-do';
    if (name === 'blockquote') return 'Empty quote';
    if (name === 'table') return '';
  }

  return defaultText ?? '';
}

export const Placeholder = Extension.create<PlaceholderOptions>({
  name: 'placeholder',

  addOptions() {
    return {
      text: "Write, '/' for commands",
      onlyWhenEmpty: false,
    };
  },

  addProseMirrorPlugins() {
    const { text, onlyWhenEmpty } = this.options;
    const editor = this.editor;

    return [
      new Plugin({
        key: new PluginKey('placeholder'),
        props: {
          decorations: (state) => {
            if (onlyWhenEmpty) {
              const first = state.doc.firstChild;
              // trailingNode appends an emptyLine, so an empty doc is never a single node
              let docIsEmpty = true;
              state.doc.forEach((child) => {
                if (!isNodeEmpty(child)) docIsEmpty = false;
              });
              if (!text || !first || !docIsEmpty) {
                return DecorationSet.empty;
              }
              return DecorationSet.create(state.doc, [
                Decoration.node(0, first.nodeSize, {
                  class: 'is-empty',
                  'data-placeholder': text,
                }),
              ]);
            }

            if (!editor.isFocused) return DecorationSet.empty;

            const { from } = state.selection;
            const $pos = state.doc.resolve(from);
            const node = $pos.parent;

            if (node.isLeaf || !isNodeEmpty(node)) return DecorationSet.empty;

            const placeholder = getNodePlaceholderText(state, text);
            if (!placeholder) return DecorationSet.empty;

            return DecorationSet.create(state.doc, [
              Decoration.node($pos.before($pos.depth), $pos.after($pos.depth), {
                class: 'is-empty',
                'data-placeholder': placeholder,
              }),
            ]);
          },
          // Focus/blur don't dispatch transactions on their own, so the
          // decoration cache stays stale. Nudge it with a no-op tr.
          handleDOMEvents: {
            focus: (view) => {
              view.dispatch(view.state.tr);
              return false;
            },
            blur: (view) => {
              view.dispatch(view.state.tr);
              return false;
            },
          },
        },
      }),
    ];
  },
});
